import React, { useMemo } from "react";
import { Card, Divider, Tag, Typography, Tooltip, Badge } from "antd";
import { 
  EuroCircleOutlined, 
  TagOutlined, 
  ArrowsAltOutlined, 
  ExperimentOutlined, 
  BoxPlotOutlined,
  FileTextOutlined 
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

// local util (kept minimal; same behavior as your page)
const fileToUrl = (file) => {
  if (!file) return "";
  return (
    file.url ||
    file.thumbUrl ||
    (file.originFileObj ? URL.createObjectURL(file.originFileObj) : "")
  );
};

export default function BedLivePreview({
  title,
  description,
  images,
  types,
  sizes,
  priceMap,
  isSuperAdmin,
  costPriceMap,
  fabrics,
  selectedFabrics,
  selectedFabricColors,
  fullFabricColors,
  pkgNonOttoman,
  pkgOttoman,
  pkgCloud,
  sizeDimensions,
}) {
  const mainImg = images?.[0] ? fileToUrl(images[0]) : null;

  const saleStats = useMemo(() => {
    const vals = Object.values(priceMap || {}).filter(
      (v) => typeof v === "number"
    );
    if (!vals.length) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    return { min, max, same: min === max };
  }, [priceMap]);

  const costStats = useMemo(() => {
    if (!isSuperAdmin) return null;
    const vals = Object.values(costPriceMap || {}).filter(
      (v) => typeof v === "number"
    );
    if (!vals.length) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    return { min, max, same: min === max };
  }, [costPriceMap, isSuperAdmin]);

  const selectedFabricObjs = useMemo(
    () => (fabrics || []).filter((f) => selectedFabrics.includes(f.id)),
    [fabrics, selectedFabrics]
  );

  const colorMapByFabric = useMemo(() => {
    const map = {};
    (selectedFabricObjs || []).forEach((f) => {
      const ofThis = (fullFabricColors || []).filter((c) => c.fabricId === f.id);
      map[f.id] = ofThis.filter((c) => selectedFabricColors.includes(c.id));
    });
    return map;
  }, [selectedFabricObjs, fullFabricColors, selectedFabricColors]);

  const dimensionRows = useMemo(() => {
    if (!sizes?.length) return [];
    return sizes.map((s) => {
      const d = sizeDimensions?.[s.id] || {};
      const clean = ["length", "width", "height"].some(
        (k) => (d[k] ?? "").toString().trim() !== ""
      );
      return { size: s.size, ...d, hasAny: clean };
    });
  }, [sizes, sizeDimensions]);

  // Enhanced image gallery with better visual feedback
// Enhanced image gallery with better visual feedback
const renderImageGallery = () => (
  <div className="space-y-3">
    {/* Main Image */}
    <div
      className="rounded-xl overflow-hidden relative group"
      style={{
        height: 220,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        border: "1px solid #eef2ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {mainImg ? (
        <>
          <img
            src={mainImg}
            alt="preview-main"
            className="transition-transform duration-300 group-hover:scale-105"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300" />
        </>
      ) : (
        <div className="text-white flex flex-col items-center">
          <div className="text-2xl mb-2">🛏️</div>
          <div>No image selected</div>
        </div>
      )}
    </div>

    {/* Thumbnails */}
    {images?.length > 1 && (
      <div className="flex gap-2 flex-wrap items-center">
        {images.slice(0, 6).map((f, i) => (
          <div
            key={f.uid || i}
            className="relative group cursor-pointer transition-all duration-200 hover:scale-110 hover:shadow-md bg-white"
            style={{
              width: 50,
              height: 50,
              borderRadius: 8,
              overflow: "hidden",
              border: "2px solid #e5e7eb",
            }}
          >
            <img
              src={fileToUrl(f)}
              alt={`thumb-${i}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200" />
          </div>
        ))}
        {images.length > 6 && (
          <Badge 
            count={`+${images.length - 6}`} 
            className="site-badge-count-4" 
            style={{ backgroundColor: '#667eea' }}
          />
        )}
      </div>
    )}
  </div>
);
  // Enhanced pricing section
  const renderPricing = () => (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
      <div className="flex items-center gap-2 mb-2">
        <EuroCircleOutlined className="text-blue-600" />
        <div className="text-sm font-semibold text-gray-700">Pricing</div>
      </div>
      
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs text-gray-500 mb-1">Sale Price</div>
          <Text className="text-lg font-bold text-emerald-600">
            {saleStats
              ? saleStats.same
                ? `€${saleStats.min}`
                : `€${saleStats.min} – €${saleStats.max}`
              : "—"}
          </Text>
        </div>
        
        {isSuperAdmin && costStats && (
          <div className="text-right">
            <div className="text-xs text-gray-500 mb-1">Cost</div>
            <Text className="text-sm font-semibold text-gray-600">
              {costStats.same
  ? `£${costStats.min}`
  : `£${costStats.min} – £${costStats.max}`}
            </Text>
          </div>
        )}
      </div>
    </div>
  );

  // Enhanced variants section
  const renderVariants = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <TagOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Variants & Dimensions</div>
      </div>
      
      {!!sizes?.length && (
        <div className="flex flex-wrap gap-2">
          {sizes.map((s) => (
            <Tag 
              key={s.id} 
              color="blue"
              className="px-3 py-1 rounded-full font-medium shadow-sm"
            >
              {s.size}
            </Tag>
          ))}
        </div>
      )}

      {/* Enhanced Dimensions Table */}
      {dimensionRows.some((r) => r.hasAny) && (
        <div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="grid grid-cols-4 text-xs font-semibold text-gray-600 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="flex items-center gap-1">
              <ArrowsAltOutlined />
              <span>Variant</span>
            </div>
            <div>Length</div>
            <div>Width</div>
            <div>Height</div>
          </div>
          {dimensionRows
            .filter((r) => r.hasAny)
            .map((r, i) => (
              <div
                key={r.size + i}
                className={`grid grid-cols-4 text-sm px-4 py-3 border-t border-gray-100 ${
                  i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                <div className="font-semibold text-gray-800">{r.size}</div>
                <div className={r.length ? "text-gray-700" : "text-gray-400"}>{r.length || "—"}</div>
                <div className={r.width ? "text-gray-700" : "text-gray-400"}>{r.width || "—"}</div>
                <div className={r.height ? "text-gray-700" : "text-gray-400"}>{r.height || "—"}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );

  // Enhanced fabrics section
  const renderFabrics = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ExperimentOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Fabrics & Colors</div>
      </div>
      
      <div className="space-y-3">
        {selectedFabricObjs.map((f) => {
          const cols = colorMapByFabric[f.id] || [];
          const show = cols.slice(0, 8);
          return (
            <div 
              key={f.id} 
              className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Text className="font-semibold text-gray-800">{f.fabric_name}</Text>
                  {typeof f.fabric_price === "number" && f.fabric_price > 0 && (
                    <span className="inline-flex items-center rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-2.5 py-0.5 text-xs font-semibold text-white shadow-sm">
                      +€{Number(f.fabric_price || 0).toFixed(2)}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {cols.length} color{cols.length !== 1 ? "s" : ""}
                </span>
              </div>
              
              {!!show.length && (
                <div className="flex flex-wrap gap-1.5">
                  {show.map((c) => (
                    <Tooltip key={c.id} title={c.color_name}>
                      <span
                        className="text-xs uppercase tracking-wide inline-flex items-center rounded-full border border-gray-300 px-2.5 py-1 font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      >
                        {c.color_name}
                      </span>
                    </Tooltip>
                  ))}
                  {cols.length > show.length && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                      +{cols.length - show.length} more
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Enhanced packaging section
  const renderPackaging = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <BoxPlotOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Packaging</div>
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Non Ottoman", value: pkgNonOttoman || 0, color: "from-blue-500 to-blue-600" },
          { label: "Ottoman", value: pkgOttoman || 0, color: "from-purple-500 to-purple-600" },
          { label: "Cloud", value: pkgCloud || 0, color: "from-indigo-500 to-indigo-600" },
        ].map((item, index) => (
          <div 
            key={index}
            className="rounded-xl p-3 text-center shadow-sm border border-gray-200 bg-white hover:shadow-md transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <div className={`text-xs font-semibold text-gray-600 mb-1`}>
              {item.label}
            </div>
            <div className={`text-lg font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent`}>
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Enhanced description section
  const renderDescription = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <FileTextOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Description</div>
      </div>
      
      <div 
        className="prose prose-sm max-w-none text-gray-700 bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </div>
  );

  return (
    <div
      className="sticky bg-transparent"
      style={{ top: 24 }}
    >
      <Card
        className="shadow-2xl rounded-2xl border-0"
        bodyStyle={{ padding: 0 }}
        title={
          <div className="flex items-center gap-2 px-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="font-bold text-gray-800">Live Preview</span>
          </div>
        }
        headStyle={{ 
          borderBottom: "1px solid #e5e7eb",
          padding: "16px 20px"
        }}
      >
        <div className="p-5 space-y-6">
          {/* Image Gallery */}
          {renderImageGallery()}

          {/* Title */}
          <div>
            <Title level={3} className="!mt-0 !mb-2 !text-gray-800">
              {title?.trim() || "Untitled Bed"}
            </Title>
            
            {/* Types */}
            {!!types?.length && (
              <div className="flex flex-wrap gap-2">
                {types.map((t) => (
                  <Tag 
                    key={t} 
                    color="geekblue"
                    className="px-3 py-1 rounded-full font-medium border-0 shadow-sm"
                  >
                    {t}
                  </Tag>
                ))}
              </div>
            )}
          </div>

          {/* Pricing */}
          {renderPricing()}

          {/* Variants & Dimensions */}
          {(!!sizes?.length || dimensionRows.some((r) => r.hasAny)) && (
            <>
              <Divider className="!my-2 !border-gray-200" />
              {renderVariants()}
            </>
          )}

          {/* Fabrics & Colors */}
          {!!selectedFabricObjs?.length && (
            <>
              <Divider className="!my-2 !border-gray-200" />
              {renderFabrics()}
            </>
          )}

          {/* Packaging */}
          <>
            <Divider className="!my-2 !border-gray-200" />
            {renderPackaging()}
          </>

          {/* Description */}
          {description?.trim() && (
            <>
              <Divider className="!my-2 !border-gray-200" />
              {renderDescription()}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}